/*
 * 戒导航 · 下载中心页面（Mithril 组件）
 * =============================================================================
 * 承载三条前端路由（由 ./extend.ts 注册、共用本组件）：
 *   downloads          → /downloads           首页（分类网格 + 最新资料表格）
 *   downloads.category → /downloads/c/:slug   分类页（工具栏 + 资料表格 + 分页）
 *   downloads.show     → /downloads/r/:slug   资料详情（简介 + 文件清单表格 + 下载）
 *
 * ★★ 渲染范式（2026-10-10 改造，用户指令）★★
 *   本页渲染在**论坛 SPA 外壳**内（与 packages/checkin 的戒打卡一致）：
 *     · 顶栏 / 侧栏 / 页脚等框架由 Flarum 提供，本组件只产出内容区；
 *     · 内容区固定用 `<div className="DownloadPage"><div className="container">…`，
 *       less/forum.less 的所有 `.jd-*` 规则都嵌套在 `.DownloadPage` 之下。
 *
 * ★★ P7「文件管理器」重构（2026-10-11，用户指令）★★
 *   用户要求：下载中心列表参考 Windows 资源管理器，高效 / 简洁 / 信息全 /
 *   空间利用率高，直接逐列显示「名称 · 类型 · 大小 · 下载数 · 浏览数 · 更新时间」。
 *   设计见 doc/知识库与文件下载中心设计方案.md §7.2（§P7.0–§P7.14）。
 *   核心变化：
 *     · **彻底移除卡片视图**（原 .jd-card-grid / resourceCard()），唯一形态 = 表格；
 *     · 两层实体「内联」为一张表（决策 P7.14-2）：
 *         - 单文件资料 → 以「文件名」成行（名称列 = 实际文件名，可直接下载）；
 *         - 多文件资料 → 以「资料标题 + N 个文件」成行（类似文件夹行，点进入详情）；
 *     · 浏览数沿用**资料级**（不下钻到文件级，决策 P7.14-3）；
 *     · 本期不做「按大小排序」（聚合排序成本高，决策 P7.14-4）。
 *   表格统一由通用组件 ./ResourceTable 渲染（列可插拔，首页 / 分类页 / 详情页三处复用）。
 *
 * ★★ 状态与重建（关键机制，别踩坑）★★
 *   DefaultResolver.makeKey() = routeName + JSON.stringify(m.route.param())，
 *   只要**路由名或参数**变化 → 组件销毁重建 → oninit 重跑。
 *   因此：
 *     · 分页 → 改路由（app.route('downloads.category', {slug, page, …})）→ 重建 → 从
 *       路由参数恢复 state，保留浏览器前进/后退语义；
 *     · 搜索 / 排序 → **绝不能改路由**（否则重建导致输入框失焦、每敲一键重挂载）。
 *       改用内部 state + window.history.replaceState 同步 URL（详见 syncUrl()）。
 *
 * ★★ 数据通道（别绕过）★★
 *   数据来自后端 DownloadDataController（forum 栈 JSON 接口）：
 *     GET {baseUrl}/downloads/data                    首页
 *     GET {baseUrl}/downloads/data/c/{slug}           分类页（支持 ?page=&sort=&dir=&q=）
 *     GET {baseUrl}/downloads/data/r/{slug}           详情页
 *   ⚠️ URL 口径：forum 自建路由挂在**站点根**，不在 /api 之下，故必须用
 *      app.forum.attribute('baseUrl') + 路径 拼接（用 app.store / apiUrl 拼会 404）。
 *
 * ★★ 响应信封与错误处理 ★★
 *   成功 { ok:true, data:{…} }   失败 { ok:false, error:{ code, message } }。
 *   app.request 默认对非 2xx 抛错并弹核心错误弹窗；这里传
 *   `errorHandler: () => undefined`（返回非 false）**抑制默认弹窗**，改由本页
 *   渲染统一风格的提示页。注意：errorHandler 只抑制弹窗，Promise 仍 reject → 必须 catch。
 *
 * ★★ 权限口径（单一事实来源在后端）★★
 *   · 能否浏览 / 能否下载由后端判（不能 → 403 FORBIDDEN → 本页提示页）；
 *   · data.canDownload / 行的 canDownload 决定列表与详情页是否给出可点的下载入口；
 *   · 下载入口用**原生 <a>**（非 SPA 路由）：它指向受控分发路由，需要一次真实的
 *     浏览器导航/下载（302 → R2 预签名直链），不能走 app.route。
 *
 * ★★ i18n ★★
 *   所有可见文案走 app.translator.trans('jiedh-downloads.forum.*')，
 *   对应 resources/locale/{zh-Hans,en}.yml，禁止硬编码中文。
 *
 * ★★ 图标口径 ★★
 *   FontAwesome 一律用 **FA5 名**（fas/far + fa-xxx），与后台权限项图标保持一致。
 *
 * ⚠️ 两个硬性禁区（本项目踩坑记录，改动前务必读）：
 *   ① 不可 import 核心的 flarum/common/components/Pagination —— 它只注册进 admin
 *      bundle，forum 侧 import 会抛「No module found」并中断其后所有扩展的注册
 *      （曾导致顶栏「博客」入口消失）。故分页自绘。
 *   ② 不可**静态** import LogInModal —— 它是懒加载 chunk；必须
 *      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))。
 */
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
// ⚠️ Dropdown 必须来自 common —— 它已注册进 **forum** bundle（本文件启用其排序下拉 / 类型多选）。
//    切勿静态 import MultiSelect / Select：它们只注册进 admin bundle，forum 侧 import 会
//    「No module found」并中断其后所有扩展注册（与文件头禁区①同类坑）。故多选自建。
import Dropdown from 'flarum/common/components/Dropdown';
import ResourceTable from './ResourceTable';

// ⚠️ 仅类型导入（编译期擦除，不产生运行时依赖）：弹窗本身按需动态 import，
//    避免把上传逻辑塞进列表首屏体积（与举报弹窗同款懒加载策略）。

import { fileTypeDef, fileTypeLabel, extensionLabel, fileTypeFor, typeKeys, canPreview } from '../utils/fileTypes';
import { formatSize } from '../utils/formatSize';

/** 列表密度偏好的 localStorage 键（本地记忆，不落后端设置项）。 */
const DENSITY_KEY = 'jiedh-downloads.list_density';

/**
 * 排序键 → 文案键后缀（P9 · R4）。
 *
 * ★ 单一契约：键集合**必须**与后端 `DownloadDataController::SORTABLE` 逐字一致
 *   （title / created_at / updated_at / published_at / download_count / view_count / size）。
 *   运行时**实际可选键以后端响应 meta.sortable 白名单为准**（前端不臆造），本表仅负责
 *   「后端排序键 → 前端文案」的映射；后端新增可排序键时，此处补一条 + 语言包补一条即可。
 * 顺序 = 排序下拉的展示顺序：上传时间 → 更新时间 → 发布时间 → 下载量 → 浏览量 → 大小 → 名称。
 */
const SORT_LABEL_KEYS = {
  created_at: 'sort_created',
  updated_at: 'sort_updated',
  published_at: 'sort_published',
  download_count: 'sort_downloads',
  view_count: 'sort_views',
  size: 'sort_size',
  title: 'sort_title'
};

/** UUID 形（8-4-4-4-12）正则，用于识别 fof/upload 的兜底文件名。 */
const UUID_BASE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * 判断展示名是否为 fof/upload 的 UUID 兜底名（形如 `<uuid>` 或 `<uuid>.pdf`）。
 *
 * 背景（见 doc/知识库与文件下载中心设计方案.md §P8.1 勘误）：**存量附件**在中文文件名
 * 场景下，被 `Str::slug` 剥空后回落写入的就是 UUID，且**原始文件名从未落库**
 * （`fof_upload_files` 无 original_name 列），故存量数据的真实文件名**不可自动还原**。
 * 本判定仅用于**存量数据的显示兜底** —— 命中时改用「资料标题」等人类可读文本作主名，
 * 避免用户对着 UUID 一头雾水；新上传文件因 `PreserveOriginalFilename` 已保留原名，不会命中。
 */
function isUuidLikeName(name) {
  if (!name) return false;
  // 去掉尾部扩展名后，剩下的主体若整体是 UUID，则判定为兜底名
  const base = name.replace(/\.[a-z0-9]+$/i, '');
  return UUID_BASE_RE.test(base);
}

/**
 * 拆出「主名 + 扩展名」两段，用于名称列展示（Windows 资源管理器风格，§P8.6）。
 *
 * 规则（以后端归一化字段 `extension` 为唯一扩展名来源，避免自造）：
 *   · 扩展名为空（如文件夹行 / 无扩展名文件）→ 原样返回，`ext` 为空串；
 *   · 展示名**已含**该扩展名（新上传文件保留了原名，`base_name` 自带 `.pdf`）
 *     → 从尾部剥离，避免渲染成「xxx.pdf.pdf」；
 *   · 展示名**不含**该扩展名（存量 UUID 兜底名回退为「资料标题」时通常不含）
 *     → 补上扩展名，满足「同时显示文件名与扩展名」的要求。
 */
function splitNameExt(name, extension) {
  const ext = String(extension ?? '').trim();
  if (!ext) return {
    base: name,
    ext: ''
  };
  const suffix = '.' + ext;
  if (name.length > suffix.length && name.slice(-suffix.length).toLowerCase() === suffix.toLowerCase()) {
    return {
      base: name.slice(0, name.length - suffix.length),
      ext: name.slice(-suffix.length)
    };
  }
  return {
    base: name,
    ext: suffix
  };
}

/* ============================================================================
 * 类型定义（字段与 DownloadDataController::index/category/show 返回结构一一对应）
 * 说明：这些是纯编译期提示；运行时字段缺失一律做了兜底，不会因后端多/少字段而崩。
 * ========================================================================== */

/** 当前页面类型（由 routeName 推导）。 */

/** 分类摘要（首页分类网格 / 分类页头部共用）。 */

/** 资料摘要（列表行 / 详情页头部共用）。 */

/** 文件条目（详情页文件清单表格）。 */

/** 分页数据（分类页；前后端只交换页码，不交换 URL）。 */

/** 列表元信息（排序契约；来自后端 meta）。 */

export default class DownloadPage extends Page {
  /**
   * 挂到 body 上的页面类名（Page 基类在 oncreate 添加、onremove 移除）。
   * 供需要「按页作用域」的样式使用；不影响功能。
   */
  bodyClass = 'App--downloads';

  /** 是否正在拉取数据（首屏显示 LoadingIndicator；后续刷新由表格骨架屏承接）。 */
  loading = true;

  /** 业务错误码（DISABLED / FORBIDDEN / NOT_FOUND / 其它）；null 表示无错误。 */
  errorCode = null;

  /** 后端返回的错误文案（已本地化，优先于前端兜底文案展示）。 */
  errorMessage = '';

  /** 当前页面的数据（index / category / show 三种结构，统一用 any 承载）。 */
  data = null;

  /** 是否正在执行「上线」操作（编辑者手动上线下线资料时禁用按钮，防重复提交）。 */
  toggling = false;

  /* ------------------------------------------------------------------
   * 列表交互状态（P7d）
   * 说明：这些**不放进 attrs / 路由**，改由内部 state + replaceState 同步 URL，
   *       以免触发 DefaultResolver 重建（输入框失焦）。分页例外，见 pager()。
   * ------------------------------------------------------------------ */

  /**
   * 列表状态：页码 / 排序键 / 方向 / 搜索词 / 类型过滤（首页与分类页共用；
   * 从路由参数初始化，见 oninit）。
   *
   * · sort/dir 为空串表示「未显式指定」→ 展示层回落后端默认（created_at desc，见 effectiveSort()）。
   * · types 为类型多选过滤（P9 · R5），键集合与后端 TYPE_KEYS / 前端 FILE_TYPES 一致。
   */
  state = (() => ({
    page: 1,
    sort: '',
    dir: '',
    q: '',
    types: []
  }))();

  /** 搜索框当前显示值（受控 input；与 state.q 解耦以支持 300ms 防抖）。 */
  searchInput = '';

  /** 搜索防抖定时器句柄（window.setTimeout 返回 number）。 */
  searchTimer = null;

  /** 列表密度偏好（本地记忆；'comfortable' 默认）。 */
  density = 'comfortable';
  oninit(vnode) {
    // Page 基类会在 oninit 里写 app.current（routeName）等，必须先调 super。
    super.oninit(vnode);

    // 组件每次「路由 / 参数变化」都会被重建（DefaultResolver.makeKey 机制），
    // 因此这里从当前路由参数恢复 state 并拉一次数据即可覆盖：进入页面、切换
    // slug、翻页等所有场景（搜索 / 排序不重建，见 syncUrl()）。
    const params = m.route.param() ?? {};
    const pageNum = parseInt(String(params.page ?? '1'), 10);
    this.state.page = isNaN(pageNum) || pageNum < 1 ? 1 : pageNum;
    this.state.sort = String(params.sort ?? '');
    const dir = String(params.dir ?? '');
    this.state.dir = dir === 'asc' || dir === 'desc' ? dir : '';
    this.state.q = String(params.q ?? '');
    this.searchInput = this.state.q;

    // 类型多选过滤（P9 · R5）：从路由 `types`（逗号分隔）还原为数组；去空白 / 去空项。
    // 非法键由后端二次过滤（parseTypes），前端只做形态还原，不在此校验白名单。
    this.state.types = String(params.types ?? '').split(',').map(s => s.trim()).filter(Boolean);

    // 密度偏好：本地记忆（读取失败静默回落默认值）
    try {
      const saved = window.localStorage.getItem(DENSITY_KEY);
      if (saved === 'compact' || saved === 'comfortable') this.density = saved;
    } catch (e) {
      /* localStorage 不可用（隐私模式等）→ 用默认密度 */
    }
    this.load();
  }

  /* ------------------------------------------------------------------
   * 派生状态
   * ------------------------------------------------------------------ */

  /** 当前页面类型。 */
  get kind() {
    const routeName = String(this.attrs.routeName ?? '');
    if (routeName === 'downloads.category') return 'category';
    if (routeName === 'downloads.show') return 'show';
    return 'index';
  }

  /** 路径参数 slug（分类页 / 详情页）。 */
  get slug() {
    return String(m.route.param('slug') ?? '');
  }

  /* ------------------------------------------------------------------
   * 工具
   * ------------------------------------------------------------------ */

  /** 站点根地址（去掉尾部斜杠，避免出现 `//`）。 */
  base() {
    return String(app.forum.attribute('baseUrl') ?? '').replace(/\/+$/, '');
  }

  /** 当前页面应请求的数据接口地址。 */
  endpoint() {
    const base = this.base();
    if (this.kind === 'category') return `${base}/downloads/data/c/${encodeURIComponent(this.slug)}`;
    if (this.kind === 'show') return `${base}/downloads/data/r/${encodeURIComponent(this.slug)}`;
    return `${base}/downloads/data`;
  }

  /** 文案翻译（统一前缀 jiedh-downloads.forum.）。 */
  t(key, params) {
    return app.translator.trans('jiedh-downloads.forum.' + key, params);
  }

  /** 文案翻译为纯字符串（用于拼进 join(' · ')、title / aria-label / placeholder 等纯文本场景）。 */
  ts(key, params) {
    // 关键修复：必须走 extract=true（trans 的第 3 个参数）拿「纯字符串」。
    // 背景：t() 返回的是 Mithril children 数组（ICU 富文本渲染用），
    //   含参数占位符的文案会渲染成数组，如 [2, ' 天前']；
    //   若用 String(数组) 转换，JS 的 Array#toString 会用 ', ' 连接各元素，
    //   于是「2 天前」被拼成「2, 天前」（多出一个逗号）。extract=true 内部走
    //   extractText() 直接拼接文本节点，得到正确的「2 天前」。
    return app.translator.trans('jiedh-downloads.forum.' + key, params, true);
  }

  /** 站内 SPA 路由地址。 */
  route(name, params) {
    return app.route(name, params);
  }

  /** 打开登录弹窗（懒加载 chunk，禁止静态 import）。 */
  login() {
    app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
  }

  /** 受控文件分发直链（原生 <a> 用；后端鉴权 / 计数 / 限速 → 302 R2 预签名直链）。 */
  downloadUrl(slug, fileId) {
    return `${this.base()}/downloads/r/${encodeURIComponent(String(slug ?? ''))}/download/${fileId}`;
  }

  /**
   * 受控文件「内联预览」直链（原生 <a target="_blank"> 用）。
   *
   * 与 downloadUrl 同源（同一分发控制器），仅路由名不同：
   *   download 路由 → 302 到 R2 上带 `response-content-disposition=attachment` 的签名直链
   *                   （浏览器触发下载，不渲染）；
   *   preview  路由 → 302 到 R2 上带 `response-content-disposition=inline` 的签名直链
   *                   （浏览器按 Content-Type 内联渲染：PDF 阅读器 / <img> / <video> / 纯文本）。
   * 两者都必须走**原生导航**，不能走 SPA 路由 / fetch（跨域 302 且 R2 未配 CORS）。
   */
  previewUrl(slug, fileId) {
    return `${this.base()}/downloads/r/${encodeURIComponent(String(slug ?? ''))}/preview/${fileId}`;
  }

  /** 元信息（排序契约）：取当前页数据里的 meta，缺省给空白名单。 */
  meta() {
    const meta = this.data?.meta ?? {};
    return {
      sort: String(meta.sort ?? ''),
      dir: meta.dir === 'asc' ? 'asc' : 'desc',
      sortable: Array.isArray(meta.sortable) ? meta.sortable : []
    };
  }

  /**
   * 时间戳字符串 → 本地 Date（兼容两种输入）。
   *
   *   ① 完整 ISO 8601：如 `2026-10-17T03:20:00+00:00`（后端新口径），浏览器按本地时区解析；
   *   ② 纯日期 `YYYY-MM-DD`：补 `T00:00:00` 按「本地零点」解析，避免被 JS 当作 UTC 零点
   *      而整体偏移一天（历史数据 / 其它来源兜底）。
   *
   * 解析失败返回 null，由调用方决定回退策略。
   */
  parseDate(value) {
    if (!value) return null;
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * 「相对时间」文案（今天 / 昨天 / N 天前 / N 个月前 / N 年前）。
   *
   * 关键修复（时区）：统一按「本地日历日」比较 —— 先把两个时间戳各自归一到本地零点，
   *   再算天数差，从而避免「UTC 日历日 vs 本地日历日」错位导致的「刚上传却显示昨天」。
   * 解析失败时原样返回，保证不会出现空白单元格。
   */
  relativeDate(value) {
    const d = this.parseDate(value);
    if (!d) return value;
    const now = new Date();
    // 归一化到「本地零点」，只保留日历日信息
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const days = Math.round((today.getTime() - that.getTime()) / 86400000);
    if (days <= 0) return this.ts('time_today');
    if (days === 1) return this.ts('time_yesterday');
    if (days < 30) return this.ts('time_days_ago', {
      count: days
    });
    if (days < 365) return this.ts('time_months_ago', {
      count: Math.floor(days / 30)
    });
    return this.ts('time_years_ago', {
      count: Math.floor(days / 365)
    });
  }

  /**
   * 时间戳字符串 → 本地「YYYY-MM-DD」（用于悬停标题 / 详情页展示的绝对日期）。
   * 解析失败时原样返回。
   */
  absoluteDate(value) {
    const d = this.parseDate(value);
    if (!d) return value;
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  /**
   * 名称列「悬停查看完整文件名」处理器（需求 E，§P8.6）。
   *
   * 参考 Windows 资源管理器：**仅当名称被截断**（内容宽度 > 可视宽度）时，才在该元素
   * 上挂原生 `title`（浏览器弹出的完整文件名小气泡）；未截断则移除，避免短名也弹提示。
   *
   * 说明：用事件 `currentTarget`（即 `.jd-name-base`）直接读 DOM 溢出量，不依赖 Mithril
   * 重绘；原生 `title` 与组件内 `.jd-date` / `.jd-changelog` 的既有做法保持一致。
   */
  overflowTitle(full) {
    return e => {
      const el = e?.currentTarget;
      if (!el) return;
      if (el.scrollWidth > el.clientWidth + 1) el.setAttribute('title', full);else el.removeAttribute('title');
    };
  }

  /**
   * 编辑者「上线」一条已下线（帖子被隐藏/删除导致）的资料。
   *
   * 走核心 JSON:API（PATCH /api/jiedh-download-resources/{id}）：
   *   · isPublished=true       —— 恢复对全体可见；
   *   · isManualOverride=true  —— 打上「手动上线」标记，后续帖子隐藏/删除的
   *      自动下线不再覆盖本资料发布态（后端 PostAttachmentCollector::applyPublication 判此标记）。
   * 完成后重新拉取详情数据（发布态以后端为准，前端不自行推断）。
   */
  online(resource) {
    if (this.toggling || !resource.id) return;
    this.toggling = true;
    m.redraw();
    app.store.find('jiedh-download-resources', resource.id).then(model => model.save({
      isPublished: true,
      isManualOverride: true
    })).then(() => {
      this.toggling = false;
      app.alerts.show({
        type: 'success'
      }, this.t('manage_online_success'));
      this.load();
    }).catch(() => {
      this.toggling = false;
      m.redraw();
      app.alerts.show({
        type: 'error'
      }, this.t('manage_online_failed'));
    });
  }

  /* ------------------------------------------------------------------
   * P10 资料举报（设计文档 §6.1 / §6.2）
   * ------------------------------------------------------------------ */

  /**
   * 详情页是否显示「举报」入口。
   *
   * 三个条件同时满足（§6.1）：
   *   ① canDownload —— 已登录且具备下载权限（游客没有举报身份可追溯）；
   *   ② 非作者本人 —— 作者不举报自己的资料；
   *   ③ 非编辑者   —— 编辑者/管理员走后台统一审核中心，前台不重复提供入口。
   * 另：已隐藏资料对普通用户不可见，能打开详情页的只有编辑者，故天然被 ③ 排除。
   */
  canReport(resource, canDownload) {
    const user = app.session.user;
    if (!canDownload || !user) return false;
    if (resource.canManage) return false;

    // authorId 缺失（异常数据）时保守放行：后端仍会做可见性与频控校验
    if (resource.authorId != null && Number(resource.authorId) === Number(user.id())) return false;
    return true;
  }

  /**
   * 打开举报弹窗。
   *
   * ★ 必须动态 import（懒加载 chunk）：举报是低频操作，不该进入详情页首屏体积；
   *   同时规避「静态 import 到未注册模块」导致整个 forum bundle 注册中断的风险。
   * 传 slug 而非 id：举报接口按 slug 解析资料（与详情数据接口同口径）。
   */
  openReport(resource) {
    app.modal.show(() => import(/* webpackChunkName: 'forum/components/ReportResourceModal', webpackMode: 'lazy-once' */ './ReportResourceModal'), {
      slug: String(resource.slug ?? ''),
      title: String(resource.title ?? '')
    });
  }

  /* ------------------------------------------------------------------
   * P11 管理员专用「上传到下载中心」
   * ------------------------------------------------------------------ */

  /**
   * 打开「上传到下载中心」弹窗（仅管理员；显隐由后端 canDirectUpload 决定）。
   *
   * 数据来源：列表接口已随包下发 `uploadCategories`（含 id、含空分类），
   * 故打开弹窗不再额外请求；分类页用 slug 反查当前分类 id 作为默认选中项
   * （分类页的 data.category 结构里没有 id，不能直接用）。
   *
   * 懒加载：上传弹窗只在管理员点按钮时才加载，普通用户永远不会下载这段代码。
   */
  openUpload() {
    const data = this.data ?? {};

    // 双保险：即便按钮因异常被渲染出来，非管理员也不会发起任何上传相关调用
    if (!data.canDirectUpload) return;
    const categories = Array.isArray(data.uploadCategories) ? data.uploadCategories : [];
    let defaultCategoryId = null;
    if (this.kind === 'category') {
      const slug = String(data.category?.slug ?? this.slug ?? '');
      const matched = categories.find(category => String(category.slug) === slug);
      if (matched) defaultCategoryId = Number(matched.id) || null;
    }
    app.modal.show(() => import(/* webpackChunkName: 'forum/components/UploadResourceModal', webpackMode: 'lazy-once' */ './UploadResourceModal'), {
      categories,
      defaultCategoryId,
      // P12：单文件上限（字节，0/null = 不限），供弹窗做上传前预校验
      maxBytes: data.uploadMaxBytes ?? null,
      // 成功后刷新当前列表（保留搜索 / 排序 / 类型过滤 / 分类上下文，D-7）
      onUploaded: () => this.load()
    });
  }

  /* ------------------------------------------------------------------
   * 数据加载
   * ------------------------------------------------------------------ */

  /**
   * 拉取当前页数据。
   *
   * 注意：**不清空 this.data**。搜索 / 排序时保留旧数据 + 表格 `loading` 骨架屏，
   *   避免整页闪白与布局跳动；首屏（data 为 null）仍走整页 LoadingIndicator。
   */
  load() {
    this.loading = true;
    this.errorCode = null;
    this.errorMessage = '';
    m.redraw();

    // 首页 / 分类页：把分页 / 排序 / 搜索 / 类型过滤拼进查询串（后端从 query params 读取）。
    // 详情页（show）不需要这些参数。
    const qs = new URLSearchParams();
    if (this.kind === 'index' || this.kind === 'category') {
      if (this.state.page > 1) qs.set('page', String(this.state.page));
      if (this.state.sort) qs.set('sort', this.state.sort);
      if (this.state.dir) qs.set('dir', this.state.dir);
      if (this.state.q) qs.set('q', this.state.q);
      if (this.state.types.length) qs.set('types', this.state.types.join(','));
    }
    const query = qs.toString();
    app.request({
      method: 'GET',
      url: this.endpoint() + (query ? '?' + query : ''),
      // 返回非 false → 抑制核心默认错误弹窗；本页自己渲染统一风格的提示页。
      errorHandler: () => undefined
    }).then(res => {
      // 成功信封形如 { ok:true, data:{…} }，取内层 data。
      this.data = res?.data ?? null;
    }).catch(e => {
      // app.request 的 RequestError.response = 后端 JSON 信封；
      // 业务错误码位于 error.response.error.code。
      const err = e?.response?.error ?? {};
      this.errorCode = String(err.code ?? 'ERROR');
      this.errorMessage = String(err.message ?? '');
    }).then(() => {
      // 相当于 finally：无论成功失败都收尾。
      this.loading = false;
      m.redraw();
    });
  }

  /* ==================================================================
   * 视图
   * ================================================================== */

  view() {
    return m("div", {
      className: "DownloadPage"
    }, m("div", {
      className: "container"
    }, this.errorCode ? this.noticeView() : !this.data ? m("div", {
      className: "jd-loading"
    }, m(LoadingIndicator, null)) : this.kind === 'show' ? this.showView() : this.kind === 'category' ? this.categoryView() : this.indexView()));
  }

  /* ------------------------------------------------------------------
   * 首页：头部 + 左侧分类导航 + 全部资料
   * （P9 · R1–R5：由「12 条只读预览」升级为「全部资料完整列表」——
   *   搜索 / 分页（每页 30）/ 排序（下载量·浏览·大小·上传·更新·发布·名称）/ 类型多选 全量生效）
   * ------------------------------------------------------------------ */

  indexView() {
    const data = this.data ?? {};
    const resources = data.resources ?? [];
    const pagination = data.pagination ?? null;
    return m("div", {
      className: "jd-index"
    }, m("div", {
      className: "jd-headrow"
    }, m("header", {
      className: "jd-hero"
    }, m("div", null, m("h1", {
      className: "jd-hero-title"
    }, data.pageTitle || this.t('page_title')), m("p", {
      className: "jd-hero-sub"
    }, this.t('resource_count', {
      count: data.total ?? 0
    })))), m("div", {
      className: "jd-section-head"
    }, m("h2", {
      className: "jd-section-title"
    }, this.t('all_resources')), this.toolbarView())), this.layoutView(m("section", {
      className: "jd-section"
    }, this.resourceTableView(resources, {
      sortable: true,
      emptyView: this.state.q || this.state.types.length ? this.searchEmptyView() : this.emptyView()
    }), pagination ? this.pager(pagination) : null), ''));
  }

  /* ------------------------------------------------------------------
   * 双栏布局：左侧分类导航树（参考 Windows 资源管理器 / 论坛侧栏）+ 右栏主内容
   * ------------------------------------------------------------------ */

  /**
   * 双栏骨架：左栏为分类导航树，右栏为页面主内容。
   *
   * 首页与分类页共用此骨架，保证两页视觉一致（侧栏位置、宽度、高亮逻辑都不变）。
   *
   * @param main       右栏主内容（Mithril vnode 或 vnode 数组）
   * @param activeSlug 当前高亮分类 slug；空串代表「全部资料」（首页）
   */
  layoutView(main, activeSlug) {
    const categories = this.data?.categories ?? [];
    return m("div", {
      className: "jd-layout"
    }, m("aside", {
      className: "jd-sidebar"
    }, this.categorySidebar(categories, activeSlug)), m("div", {
      className: "jd-main"
    }, main));
  }

  /**
   * 分类导航树：置顶「全部资料」入口 + 各分类项。
   *
   * 计数口径：后端 navTotal / category.count 均为「当前用户可见资料数」，
   * 与右栏表格实际可列出的条数一致 —— 不会出现「看得见分类、点进去却为空」。
   * 空分类已在后端过滤，故此处不再判空。
   */
  categorySidebar(categories, activeSlug) {
    // 注意：Mithril 要求同一 fragment 内的 vnode「要么全部带 key、要么全部不带 key」，
    // 因此这里必须把「全部资料」项与各分类项合并成「同一个扁平数组」再交给 nav，
    // 否则会出现「带 key 项 + 不带 key 项」混用而抛 TypeError（页面卡在 loading）。
    const items = [this.categoryNavItem({
      name: this.t('all_resources'),
      slug: '',
      icon: 'fas fa-layer-group',
      color: '',
      description: '',
      count: this.data?.navTotal ?? 0
    }, activeSlug === ''), ...categories.map(category => this.categoryNavItem(category, activeSlug === category.slug))];
    return m("nav", {
      className: "jd-catnav",
      "aria-label": this.t('all_categories')
    }, items);
  }

  /**
   * 单个分类导航项：图标 + 名称 + 数量徽标。
   *
   * 用 LinkButton（而非 Button）确保走 Flarum SPA 路由、不整页刷新；
   * 主题色走 --jd-cat-color，当前项以 .is-active 高亮并带 aria-current="page"（可访问性）。
   * 「全部资料」与普通分类的唯一差异是 href（前者回首页），故复用同一渲染。
   */
  categoryNavItem(category, active) {
    return m(LinkButton, {
      key: category.slug || '__all__',
      className: 'jd-catnav-item' + (active ? ' is-active' : ''),
      href: category.slug ? this.route('downloads.category', {
        slug: category.slug
      }) : this.route('downloads'),
      style: category.color ? {
        '--jd-cat-color': category.color
      } : undefined,
      "aria-current": active ? 'page' : undefined
    }, m("span", {
      className: "jd-catnav-icon"
    }, m(Icon, {
      name: category.icon || 'fas fa-folder'
    })), m("span", {
      className: "jd-catnav-name"
    }, category.name), m("span", {
      className: "jd-catnav-count"
    }, category.count));
  }

  /* ------------------------------------------------------------------
   * 分类页：头部 + 左栏导航 + 右栏（工具栏 + 资料表格 + 分页）
   * ------------------------------------------------------------------ */

  categoryView() {
    const data = this.data ?? {};
    const category = data.category ?? {};
    const resources = data.resources ?? [];
    const pagination = data.pagination ?? null;
    return m("div", {
      className: "jd-category-page"
    }, m("header", {
      className: "jd-hero jd-hero-category",
      style: {
        '--jd-cat-color': category.color || ''
      }
    }, m("div", null, m("h1", {
      className: "jd-hero-title"
    }, category.name), category.description ? m("p", {
      className: "jd-hero-desc"
    }, category.description) : null, m("p", {
      className: "jd-hero-sub"
    }, this.t('resource_count', {
      count: data.total ?? 0
    })))), this.layoutView(m('[', null, this.toolbarView(), this.resourceTableView(resources, {
      sortable: true,
      emptyView: this.state.q || this.state.types.length ? this.searchEmptyView() : this.emptyView()
    }), pagination ? this.pager(pagination) : null), category.slug || ''));
  }

  /* ------------------------------------------------------------------
   * 详情页：头部 + 简介 + 文件清单表格 + 下载
   * ------------------------------------------------------------------ */

  showView() {
    const data = this.data ?? {};
    const resource = data.resource ?? {};
    const files = data.files ?? [];
    const canDownload = !!data.canDownload;
    return m("div", {
      className: "jd-detail"
    }, m("header", {
      className: "jd-detail-header"
    }, m("div", {
      className: "jd-detail-heading"
    }, resource.categoryName ? m(LinkButton, {
      className: "jd-badge",
      href: this.route('downloads.category', {
        slug: resource.categorySlug
      }),
      style: {
        '--jd-badge-color': resource.categoryColor || ''
      }
    }, resource.categoryName) : null, resource.canManage && !resource.isPublished ? m("span", {
      className: "jd-badge jd-badge-offline"
    }, m(Icon, {
      name: "fas fa-eye-slash"
    }), this.t('status_offline')) : null, resource.canManage && resource.isHidden ? m("span", {
      className: "jd-badge jd-badge-hidden"
    }, m(Icon, {
      name: "fas fa-eye-slash"
    }), this.t('status_hidden')) : null, m("h1", {
      className: "jd-detail-title"
    }, resource.title), m("div", {
      className: "jd-detail-meta"
    }, resource.version ? m("span", {
      className: "jd-meta-item"
    }, m(Icon, {
      name: "fas fa-code-branch"
    }), this.t('version_label'), " ", resource.version) : null, m("span", {
      className: "jd-meta-item"
    }, m(Icon, {
      name: "fas fa-download"
    }), this.t('download_count_label', {
      count: resource.downloadCount ?? 0
    })), m("span", {
      className: "jd-meta-item"
    }, m(Icon, {
      name: "far fa-eye"
    }), this.t('view_count_label', {
      count: resource.viewCount ?? 0
    })), resource.publishedAt ? m("span", {
      className: "jd-meta-item"
    }, m(Icon, {
      name: "far fa-clock"
    }), this.absoluteDate(resource.publishedAt)) : null, resource.authorName ? m("span", {
      className: "jd-meta-item"
    }, m(Icon, {
      name: "far fa-user"
    }), resource.authorName) : null), resource.discussionId && resource.postNumber ? m("div", {
      className: "jd-detail-source"
    }, m(LinkButton, {
      className: "jd-btn jd-btn-ghost",
      href: app.route('discussion.near', {
        id: resource.discussionId,
        near: resource.postNumber
      })
    }, m(Icon, {
      name: "fas fa-comments"
    }), this.t('action_view_source_post'))) : null, this.canReport(resource, canDownload) ? m("div", {
      className: "jd-detail-actions"
    }, m("button", {
      type: "button",
      className: "jd-act jd-act-ghost",
      onclick: () => this.openReport(resource)
    }, m(Icon, {
      name: "far fa-flag"
    }), m("span", null, this.t('action_report')))) : null, resource.canManage && !resource.isPublished ? m("div", {
      className: "jd-detail-manage"
    }, m("span", {
      className: "jd-manage-hint"
    }, this.t('manage_offline_hint')), m(Button, {
      className: "jd-btn jd-btn-primary",
      disabled: this.toggling,
      loading: this.toggling,
      onclick: () => this.online(resource)
    }, m(Icon, {
      name: "fas fa-eye"
    }), this.t('action_online'))) : null)), data.descriptionHtml ? m("div", {
      className: "jd-description"
    }, m.trust(data.descriptionHtml)) : null, m("section", {
      className: "jd-section"
    }, m("h2", {
      className: "jd-section-title"
    }, this.t('file_count_label', {
      count: files.length
    })), !canDownload ? this.gateView() : null, m(ResourceTable, {
      items: files,
      columns: this.fileColumns(resource, canDownload),
      rowKey: item => item.id,
      density: this.density,
      loading: this.loading,
      emptyView: this.emptyView()
    })));
  }

  /* ==================================================================
   * 列定义（扩展点：新增列只加一条 def；ResourceTable 自动渲染）
   * ================================================================== */

  /**
   * 资料列表列（首页 / 分类页共用）。
   * 桌面展示 7 列：名称 · 类型 · 大小 · 下载 · 浏览 · 更新 · 操作；
   * 窄屏按 priority 逐级隐藏次要列，并把关键信息折叠到名称下方小字（.jd-name-sub）。
   */
  resourceColumns() {
    return [{
      key: 'name',
      labelKey: 'column_name',
      priority: 1,
      sortKey: 'title',
      render: item => this.nameCell(item)
    }, {
      key: 'type',
      labelKey: 'column_type',
      priority: 2,
      width: '116px',
      render: item => this.typeCell(item)
    }, {
      key: 'size',
      labelKey: 'column_size',
      priority: 2,
      width: '92px',
      align: 'right',
      sortKey: 'size',
      render: item => this.sizeCell(item)
    }, {
      key: 'downloads',
      labelKey: 'column_downloads',
      priority: 2,
      width: '88px',
      align: 'right',
      sortKey: 'download_count',
      render: item => this.numCell(item.downloadCount)
    }, {
      key: 'views',
      labelKey: 'column_views',
      priority: 2,
      width: '88px',
      align: 'right',
      sortKey: 'view_count',
      render: item => this.numCell(item.viewCount)
    }, {
      key: 'updated',
      labelKey: 'column_updated',
      priority: 3,
      width: '110px',
      align: 'right',
      sortKey: 'updated_at',
      render: item => this.dateCell(item.updatedAt)
    }, {
      key: 'actions',
      labelKey: 'column_actions',
      priority: 1,
      width: '140px',
      align: 'right',
      render: item => this.actionsCell(item)
    }];
  }

  /**
   * 详情页文件清单列：名称 · 类型 · 大小 · 版本 · 更新说明 · 操作。
   * （本期不做文件级排序：文件数通常很少，且主文件优先的固定序更符合直觉。）
   */
  fileColumns(resource, canDownload) {
    return [{
      key: 'name',
      labelKey: 'column_name',
      priority: 1,
      render: file => this.fileNameCell(file, resource)
    }, {
      key: 'type',
      labelKey: 'column_type',
      priority: 2,
      width: '116px',
      render: file => this.typeCell(file)
    }, {
      key: 'size',
      labelKey: 'column_size',
      priority: 2,
      width: '92px',
      align: 'right',
      render: file => this.sizeCell(file)
    }, {
      key: 'version',
      labelKey: 'column_version',
      priority: 3,
      width: '110px',
      render: file => file.version ? m("span", {
        className: "jd-text"
      }, file.version) : m("span", {
        className: "jd-muted"
      }, "\u2014")
    }, {
      key: 'changelog',
      labelKey: 'column_changelog',
      priority: 3,
      render: file => file.changelog ? m("span", {
        className: "jd-changelog",
        title: file.changelog
      }, file.changelog) : m("span", {
        className: "jd-muted"
      }, "\u2014")
    }, {
      key: 'actions',
      labelKey: 'column_actions',
      priority: 1,
      // 详情页文件行可能同时出现「预览 + 下载」两个按钮，列宽按两钮并排预留（180px）。
      width: '180px',
      align: 'right',
      render: file => this.fileActionsCell(file, resource, canDownload)
    }];
  }

  /* ==================================================================
   * 单元格渲染（三处表格共用）
   * ================================================================== */

  /**
   * 名称列（资料行）。
   *
   * 内联两层实体（§P7.14 决策 2）：
   *   · 单文件资料 → 名称列直接显示**文件名**（等同「文件行」，可直接下载）；
   *   · 多文件资料 → 名称列显示**资料标题 + 「N 个文件」**（等同「文件夹行」，进入详情）。
   * 名称下方 `.jd-name-sub` 为窄屏折叠信息（桌面 CSS 隐藏），保持小屏信息不丢。
   */
  nameCell(item) {
    const isFile = item.filesCount === 1 && !!item.primaryFileName;
    // 存量兜底（§P8.1）：单文件资料若文件名是 UUID 兜底名（原始名从未落库、不可还原），
    // 则以「资料标题」作主名、UUID 名退居副文本，避免用户看不出这是什么文件。
    const uuidLike = isFile && isUuidLikeName(item.primaryFileName);
    const name = uuidLike ? item.title : isFile ? item.primaryFileName : item.title;
    const type = item.fileType || fileTypeFor(item.extension);
    const def = fileTypeDef(type);

    // 窄屏折叠小字：存量命中时回显真实（UUID）文件名；单文件行补资料标题；
    // 其余拼「大小 · 下载 · 浏览 · 分类 · 更新时间」
    const parts = [];
    if (uuidLike) parts.push(item.primaryFileName);else if (isFile && item.title && item.title !== name) parts.push(item.title);
    const size = item.sizeText || formatSize(item.sizeBytes);
    if (size) parts.push(size);
    parts.push(this.ts('download_count_label', {
      count: item.downloadCount ?? 0
    }));
    parts.push(this.ts('view_count_label', {
      count: item.viewCount ?? 0
    }));
    if (item.categoryName) parts.push(item.categoryName);
    const rel = this.relativeDate(item.updatedAt);
    if (rel) parts.push(rel);

    // 名称列拆「主名 + 扩展名」（§P8.6）：仅单文件行补扩展名（文件夹行不带扩展名）
    const {
      base,
      ext
    } = splitNameExt(name, isFile ? item.extension : null);
    return m("div", {
      className: "jd-name"
    }, m("span", {
      className: "jd-name-icon",
      style: {
        '--jd-type-tone': def.tone
      }
    }, m(Icon, {
      name: def.icon
    })), m("span", {
      className: "jd-name-main"
    }, m("span", {
      className: "jd-name-text"
    }, m("span", {
      className: "jd-name-label"
    }, m("span", {
      className: "jd-name-base",
      onmouseenter: this.overflowTitle(base + ext)
    }, base), ext ? m("span", {
      className: "jd-name-ext"
    }, ext) : null), item.filesCount >= 2 ? m("span", {
      className: "jd-chip"
    }, this.t('label_files_count', {
      count: item.filesCount
    })) : null, item.canManage && !item.isPublished ? m("span", {
      className: "jd-badge jd-badge-offline"
    }, m(Icon, {
      name: "fas fa-eye-slash"
    }), this.t('status_offline')) : null), parts.length ? m("span", {
      className: "jd-name-sub"
    }, parts.join(' · ')) : null));
  }

  /** 名称列（详情页文件行）：文件图标 + 展示名 + 主文件徽标 + 真实文件名（窄屏折叠小字）。 */
  fileNameCell(file, resource) {
    const type = file.fileType || fileTypeFor(file.extension);
    const def = fileTypeDef(type);

    // 存量兜底（§P8.1）：标签是 UUID 兜底名时，单文件资料改用「资料标题」作主名
    // （多文件时标题无法区分各文件，故保持原样，仅回显真实文件名于副文本）。
    const singleFile = (resource?.filesCount ?? 1) === 1;
    const label = isUuidLikeName(file.label) && singleFile && resource?.title ? resource.title : file.label;

    // 名称列拆「主名 + 扩展名」（§P8.6）：扩展名取该文件自身（folder 类型无扩展名则不显示）
    const {
      base,
      ext
    } = splitNameExt(label, file.extension);
    return m("div", {
      className: "jd-name"
    }, m("span", {
      className: "jd-name-icon",
      style: {
        '--jd-type-tone': def.tone
      }
    }, m(Icon, {
      name: def.icon
    })), m("span", {
      className: "jd-name-main"
    }, m("span", {
      className: "jd-name-text"
    }, m("span", {
      className: "jd-name-label"
    }, m("span", {
      className: "jd-name-base",
      onmouseenter: this.overflowTitle(base + ext)
    }, base), ext ? m("span", {
      className: "jd-name-ext"
    }, ext) : null), file.isPrimary ? m("span", {
      className: "jd-chip"
    }, this.t('primary_file')) : null), file.fileName && file.fileName !== label ? m("span", {
      className: "jd-name-sub"
    }, file.fileName) : null));
  }

  /**
   * 类型列（资料行 / 文件行通用）。
   * 主文本用大写扩展名（Windows 资源管理器「类型」列风格）；无扩展名回落类型中文案
   * （多文件行 → 文件夹；其余 → 文件）。
   */
  typeCell(item) {
    const type = item.fileType || fileTypeFor(item.extension);
    const def = fileTypeDef(type);
    const text = extensionLabel(item.extension, type) || fileTypeLabel(type);
    return m("span", {
      className: "jd-type-badge",
      style: {
        '--jd-type-tone': def.tone
      }
    }, m(Icon, {
      name: def.icon
    }), m("span", {
      className: "jd-type-text"
    }, text));
  }

  /** 大小列（资料行 / 文件行通用）：优先后端 sizeText，其次 size，再兜底 sizeBytes。 */
  sizeCell(item) {
    const text = item.sizeText || item.size || formatSize(item.sizeBytes);
    return text ? m("span", {
      className: "jd-size"
    }, text) : m("span", {
      className: "jd-muted"
    }, "\u2014");
  }

  /** 数值列（下载数 / 浏览数）。 */
  numCell(value) {
    return m("span", {
      className: "jd-num"
    }, Number(value ?? 0));
  }

  /** 日期列（相对时间 + 本地绝对日期 title）。 */
  dateCell(iso) {
    if (!iso) return m("span", {
      className: "jd-muted"
    }, "\u2014");
    return m("span", {
      className: "jd-date",
      title: this.absoluteDate(iso)
    }, this.relativeDate(iso));
  }

  /**
   * 操作列（资料行）。
   *   · 来源帖子（自动收录资料）→ 原帖快捷入口（ghost 图标按钮）；
   *   · 单文件资料 → 直接下载（无权限时给登录 / 锁定提示）；
   *   · 多文件 / 无文件资料 → 「进入」详情（LinkButton，SPA 跳转）。
   * 整行点击进入详情由 ResourceTable.handleRowActivate 兜底（点 a/button 时不触发）。
   */
  actionsCell(item) {
    const nodes = [];
    if (item.discussionId && item.postNumber) {
      nodes.push(m(LinkButton, {
        className: "jd-act jd-act-ghost",
        href: app.route('discussion.near', {
          id: item.discussionId,
          near: item.postNumber
        }),
        title: this.ts('action_view_source_post'),
        "aria-label": this.ts('action_view_source_post')
      }, m(Icon, {
        name: "fas fa-comments"
      })));
    }
    if (item.filesCount === 1 && item.primaryFileId) {
      if (item.canDownload) {
        // 原生 <a>：下载需要真实浏览器导航（302 → R2 预签名直链），不能走 SPA 路由。
        nodes.push(m("a", {
          className: "jd-act jd-act-primary",
          href: this.downloadUrl(item.slug, item.primaryFileId),
          rel: "nofollow"
        }, m(Icon, {
          name: "fas fa-download"
        }), m("span", null, this.t('action_download'))));
      } else if (!app.session.user) {
        nodes.push(m("button", {
          type: "button",
          className: "jd-act jd-act-primary",
          onclick: () => this.login()
        }, m(Icon, {
          name: "fas fa-download"
        }), m("span", null, this.t('action_login_to_download'))));
      } else {
        nodes.push(m("span", {
          className: "jd-act jd-act-locked"
        }, m(Icon, {
          name: "fas fa-lock"
        }), m("span", null, this.t('action_download'))));
      }
    } else {
      nodes.push(m(LinkButton, {
        className: "jd-act jd-act-ghost",
        href: this.route('downloads.show', {
          slug: item.slug
        })
      }, m(Icon, {
        name: "fas fa-folder-open"
      }), m("span", null, this.t('action_enter'))));
    }
    return m("div", {
      className: "jd-actions"
    }, nodes);
  }

  /**
   * 操作列（详情页文件行）。
   *   · 有下载权限 + 浏览器可预览 → 「预览」（新标签页，受控 302 → R2 inline 直链）+「下载」；
   *   · 有下载权限 + 不可预览     → 仅「下载」；
   *   · 无下载权限               → 锁定提示（不给出任何真实入口，与列表口径一致）。
   * 两个入口都用**原生 <a>**：分发路由是受控 302 跳转 + 计数 / 限速，必须真实浏览器导航。
   */
  fileActionsCell(file, resource, canDownload) {
    if (!canDownload) {
      return m("span", {
        className: "jd-act jd-act-locked"
      }, m(Icon, {
        name: "fas fa-lock"
      }), m("span", null, this.t('action_download')));
    }
    const slug = String(resource.slug ?? '');
    const nodes = [];

    // 「预览」仅在浏览器原生可渲染时给出（PDF / 图片 / 音视频 / 纯文本）；
    // 新标签页打开 —— 不打断当前详情页浏览，也便于用户对照信息。
    if (canPreview(file.extension, file.fileType)) {
      nodes.push(m("a", {
        className: "jd-act jd-act-ghost",
        href: this.previewUrl(slug, file.id),
        target: "_blank",
        rel: "nofollow noopener noreferrer"
      }, m(Icon, {
        name: "far fa-eye"
      }), m("span", null, this.t('action_preview'))));
    }
    nodes.push(m("a", {
      className: "jd-act jd-act-primary",
      href: this.downloadUrl(slug, file.id),
      rel: "nofollow"
    }, m(Icon, {
      name: "fas fa-download"
    }), m("span", null, this.t('action_download'))));
    return m("div", {
      className: "jd-actions"
    }, nodes);
  }

  /* ==================================================================
   * 表格 / 工具栏
   * ================================================================== */

  /**
   * 资料表格（首页 / 分类页复用）。
   * 首页与分类页均传 sortable=true → 表头排序可用（可排序列以后端 meta.sortable 白名单为准）。
   */
  resourceTableView(resources, opts) {
    if (opts === void 0) {
      opts = {};
    }
    const canSort = !!opts.sortable;
    return m(ResourceTable, {
      items: resources,
      columns: this.resourceColumns(),
      rowKey: item => item.slug
      // 用「有效排序键 / 方向」：state 未显式设置时回落后端默认（created_at desc），
      // 使表头高亮与排序下拉标签在首屏就与后端真实排序完全一致。
      ,
      sort: canSort ? this.effectiveSort() : undefined,
      dir: canSort ? this.effectiveDir() : undefined,
      sortable: canSort ? this.meta().sortable : [],
      onSort: canSort ? key => this.onSort(key) : undefined,
      density: this.density,
      loading: this.loading,
      rowHref: item => this.route('downloads.show', {
        slug: item.slug
      }),
      emptyView: opts.emptyView
    });
  }

  /**
   * 列表工具栏（首页 / 分类页复用）：搜索框（300ms 防抖）+ 排序下拉 + 方向切换 + 类型多选 + 密度。
   *
   * ★ 交互原则（P9 · R1–R5）：搜索 / 排序 / 方向 / 类型过滤**均不改路由**（内部 state +
   *   replaceState，见 syncUrl()），避免 DefaultResolver 因参数变化销毁重建组件导致输入框失焦；
   *   仅分页改路由（见 pager()，翻页后由 oninit 从路由参数还原 state）。
   */
  toolbarView() {
    return m("div", {
      className: "jd-toolbar"
    }, m("div", {
      className: "jd-search"
    }, m(Icon, {
      name: "fas fa-search",
      className: "jd-search-icon"
    }), m("input", {
      type: "search",
      className: "jd-search-input",
      value: this.searchInput,
      placeholder: this.ts('search_placeholder'),
      oninput: e => this.onSearchInput(e.target.value),
      onkeydown: e => {
        // 回车立即检索（跳过防抖等待）
        if (e.key === 'Enter') {
          e.preventDefault();
          this.applySearchNow();
        }
      }
    }), this.searchInput ? m("button", {
      type: "button",
      className: "jd-search-clear",
      onclick: () => this.clearSearch(),
      "aria-label": this.ts('search_clear')
    }, m(Icon, {
      name: "fas fa-times"
    })) : null), m("div", {
      className: "jd-toolbar-right"
    }, this.sortDropdownView(), this.sortDirToggleView(), this.typeFilterView(), m("button", {
      type: "button",
      className: "jd-density-btn",
      onclick: () => this.toggleDensity(),
      title: this.ts('density_toggle'),
      "aria-label": this.ts('density_toggle')
    }, m(Icon, {
      name: this.density === 'compact' ? 'fas fa-bars' : 'fas fa-list'
    })), this.data?.canDirectUpload ? m(Button, {
      className: "Button Button--primary jd-upload-btn",
      type: "button",
      icon: "fas fa-cloud-upload-alt",
      onclick: () => this.openUpload()
    }, this.t('upload_button')) : null));
  }

  /* ==================================================================
   * 工具栏控件（P9 · R2–R5）：排序下拉 / 方向切换 / 类型多选
   * ================================================================== */

  /**
   * 排序下拉（单选）。选后自动收起 —— 故 onclick 内**不**阻止冒泡，让 Bootstrap dropdown 正常关闭。
   * 选项以后端 meta.sortable 白名单为准（见 sortOptions()），当前项以 check 图标标记。
   */
  sortDropdownView() {
    const active = this.effectiveSort();
    return m(Dropdown, {
      buttonClassName: "Button jd-sort-btn",
      menuClassName: "Dropdown-menu jd-sort-menu",
      icon: "fas fa-sort-amount-down",
      label: this.ts(SORT_LABEL_KEYS[active] ?? 'sort_created'),
      accessibleToggleLabel: this.ts('sort_label')
    }, this.sortOptions().map(opt => m(Button, {
      type: "button",
      className: "Dropdown-item"
      // 选中态视觉用 check / empty 图标（与核心 MultiSelect 同款）：
      // 核心 Button **不消费 `active` prop**，故不依赖 active class 做标记。
      ,
      icon: opt.key === active ? 'fas fa-check' : 'fas fa-empty',
      onclick: () => this.changeSort(opt.key)
    }, this.t(opt.labelKey))));
  }

  /**
   * 排序方向切换按钮（升序 / 降序）。与表头排序、排序下拉共享同一 state（state.dir），
   * 三者任意一处改动都会即时反映到其余两处（见 effectiveDir()）。
   */
  sortDirToggleView() {
    const dir = this.effectiveDir();
    return m("button", {
      type: "button",
      className: "jd-sortdir-btn",
      onclick: () => this.toggleDir(),
      title: this.ts(dir === 'asc' ? 'sort_asc' : 'sort_desc'),
      "aria-label": this.ts('sort_direction')
    }, m(Icon, {
      name: dir === 'asc' ? 'fas fa-sort-amount-up' : 'fas fa-sort-amount-down'
    }));
  }

  /**
   * 类型多选下拉（P9 · R5）。
   *
   * ⚠️ 用核心 Dropdown **自建**而非核心 MultiSelect：后者只注册进 admin bundle，forum 侧
   *    import 会「No module found」并中断其后所有扩展注册（与文件头禁区①同类坑）。
   * ⚠️ 多选需**保持菜单展开**：onclick 内 e.stopPropagation() 阻止冒泡到 dropdown-toggle
   *    （否则每次点选都会被 Bootstrap 收起菜单）。单选无此问题（见 sortDropdownView）。
   * 选中态视觉用 check / empty 图标标记；已选 ≥1 项时追加「清空」项。
   */
  typeFilterView() {
    const selected = this.state.types;
    const label = selected.length ? selected.map(k => fileTypeLabel(k)).join('、') : this.ts('filter_type_all');
    return m(Dropdown, {
      buttonClassName: "Button jd-filter-btn",
      menuClassName: "Dropdown-menu jd-filter-menu",
      icon: "fas fa-filter",
      label: label,
      accessibleToggleLabel: this.ts('filter_type')
    }, typeKeys().map(key => m(Button, {
      type: "button",
      className: "Dropdown-item",
      icon: selected.indexOf(key) !== -1 ? 'fas fa-check' : 'fas fa-empty',
      onclick: e => {
        e.stopPropagation();
        this.toggleType(key);
      }
    }, fileTypeLabel(key))), selected.length ? m(Button, {
      type: "button",
      className: "Dropdown-item jd-filter-clear",
      icon: "fas fa-times",
      onclick: e => {
        e.stopPropagation();
        this.clearTypes();
      }
    }, this.t('filter_clear')) : null);
  }

  /* ==================================================================
   * 交互（搜索 / 排序 / 类型过滤 / 密度）
   * ================================================================== */

  /* ------------------------------------------------------------------
   * 排序 / 过滤：有效值派生（前端展示与后端真实排序保持一致的口径）
   * ------------------------------------------------------------------ */

  /**
   * 有效排序键：state 未显式指定时，回落后端默认排序（meta.sort，通常 created_at）。
   * 取值优先级：state.sort（用户已选） → meta.sort（后端默认） → 'created_at'（兜底常量）。
   */
  effectiveSort() {
    return this.state.sort || this.meta().sort || 'created_at';
  }

  /**
   * 有效排序方向：state 未显式指定时，回落后端默认方向（meta.dir，通常 desc = 时间倒序）。
   * 取值优先级：state.dir（用户已选） → meta.dir（后端默认） → 'desc'（兜底常量）。
   */
  effectiveDir() {
    return this.state.dir || this.meta().dir || 'desc';
  }

  /**
   * 排序下拉选项：**以后端响应 meta.sortable 白名单为准**（前端不臆造可排序列），
   * 再按 SORT_LABEL_KEYS 的声明顺序（上传→更新→发布→下载→浏览→大小→名称）展示。
   * 后端未给白名单时（异常情况）退化为展示全部已知键，避免下拉为空。
   */
  sortOptions() {
    const allowed = this.meta().sortable.length ? this.meta().sortable : Object.keys(SORT_LABEL_KEYS);
    return Object.keys(SORT_LABEL_KEYS).filter(k => allowed.indexOf(k) !== -1).map(k => ({
      key: k,
      labelKey: SORT_LABEL_KEYS[k]
    }));
  }

  /**
   * 选择排序键（排序下拉调用）：回到第 1 页；标题列默认升序（A→Z 更符合直觉），
   * 其余列默认降序（数值 / 时间「大的在前」更符合直觉）。与表头排序共享同一 state。
   */
  changeSort(key) {
    // 重复选择当前排序键 → 视为无操作（避免把已调好的方向重置回默认）
    if (key === this.effectiveSort()) return;
    this.state.sort = key;
    this.state.dir = key === 'title' ? 'asc' : 'desc';
    this.state.page = 1;
    this.syncUrl();
    this.load();
    m.redraw();
  }

  /** 切换排序方向（升↔降）。与表头 / 排序下拉共享 state.dir。 */
  toggleDir() {
    this.state.dir = this.effectiveDir() === 'asc' ? 'desc' : 'asc';
    this.state.page = 1;
    this.syncUrl();
    this.load();
    m.redraw();
  }

  /** 类型多选：切换单个类型键的选中态；任意变更都回到第 1 页重新拉数。 */
  toggleType(key) {
    const i = this.state.types.indexOf(key);
    this.state.types = i === -1 ? this.state.types.concat(key) : this.state.types.filter(k => k !== key);
    this.state.page = 1;
    this.syncUrl();
    this.load();
    m.redraw();
  }

  /** 清空类型过滤（已选为空时无操作）。 */
  clearTypes() {
    if (!this.state.types.length) return;
    this.state.types = [];
    this.state.page = 1;
    this.syncUrl();
    this.load();
    m.redraw();
  }

  /* ------------------------------------------------------------------
   * 搜索
   * ------------------------------------------------------------------ */

  /** 输入即时反馈：更新受控值 + 触发重绘（重绘不重置 value，焦点不丢）+ 300ms 防抖检索。 */
  onSearchInput(value) {
    this.searchInput = value;
    m.redraw();
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.searchTimer = null;
      this.applySearch(this.searchInput);
    }, 300);
  }

  /** 回车立即检索：取消未触发的防抖并同步执行。 */
  applySearchNow() {
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.applySearch(this.searchInput);
  }

  /** 执行检索：词未变则不动；变了则回到第 1 页、同步 URL、重新拉数。 */
  applySearch(raw) {
    const next = String(raw ?? '').trim();
    if (next === this.state.q) return;
    this.state.q = next;
    this.state.page = 1;
    this.syncUrl();
    this.load();
  }

  /** 清空搜索。 */
  clearSearch() {
    this.searchInput = '';
    if (this.searchTimer !== null) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    this.applySearch('');
  }

  /**
   * 表头排序：与排序下拉 + 方向切换按钮**共享同一 state**（三者永远一致）。
   * · 同列 → 切换方向；换列 → 标题列默认升序、其余默认降序（数值列降序更符合直觉）。
   * · 排序不重建组件（只改内部 state + replaceState），故输入框焦点与滚动位置都不丢。
   */
  onSort(key) {
    if (key === this.effectiveSort()) {
      // 同列：显式落 state.sort（保证 URL 可分享），并翻转方向
      this.state.sort = key;
      this.state.dir = this.effectiveDir() === 'asc' ? 'desc' : 'asc';
    } else {
      this.state.sort = key;
      this.state.dir = key === 'title' ? 'asc' : 'desc';
    }
    this.state.page = 1;
    this.syncUrl();
    this.load();
  }

  /** 密度切换（本地记忆）。 */
  toggleDensity() {
    this.density = this.density === 'compact' ? 'comfortable' : 'compact';
    try {
      window.localStorage.setItem(DENSITY_KEY, this.density);
    } catch (e) {
      /* localStorage 不可用 → 仅本次会话生效 */
    }
    m.redraw();
  }

  /**
   * 同步 URL（原地替换，不触发路由重建）。
   *
   * 手拼查询串（仅保留非默认值：q / types / sort / dir / page>1），并用 replaceState 写入，
   * 既让地址栏可分享 / 可刷新还原，又不让 DefaultResolver 因参数变化销毁重建组件。
   */
  syncUrl() {
    const parts = [];
    if (this.state.q) parts.push('q=' + encodeURIComponent(this.state.q));
    if (this.state.types.length) parts.push('types=' + encodeURIComponent(this.state.types.join(',')));
    if (this.state.sort) parts.push('sort=' + encodeURIComponent(this.state.sort));
    if (this.state.dir) parts.push('dir=' + encodeURIComponent(this.state.dir));
    if (this.state.page > 1) parts.push('page=' + String(this.state.page));
    const url = window.location.pathname + (parts.length ? '?' + parts.join('&') : '');
    window.history.replaceState(null, '', url);
  }

  /* ==================================================================
   * 复用片段
   * ================================================================== */

  /** 空态（无数据 / 无可见资料）。 */
  emptyView() {
    return m("div", {
      className: "jd-empty"
    }, m(Icon, {
      name: "far fa-file"
    }), m("p", null, this.t('empty_list')));
  }

  /** 搜索无结果空态（与「本就没有资料」区分，给出更准确的提示）。 */
  searchEmptyView() {
    return m("div", {
      className: "jd-empty"
    }, m(Icon, {
      name: "fas fa-search"
    }), m("p", null, this.t('search_empty')));
  }

  /** 下载门槛提示条（不可下载时显示）。 */
  gateView() {
    const isGuest = !app.session.user;
    return m("div", {
      className: "jd-gate"
    }, m(Icon, {
      name: "fas fa-lock"
    }), m("span", null, isGuest ? this.t('download_login_required') : this.t('download_forbidden')), isGuest ? m(Button, {
      className: "jd-btn jd-btn-primary",
      onclick: () => this.login()
    }, this.t('action_login')) : null);
  }

  /**
   * 自绘极简分页条。
   *
   * ⚠️ 为什么不用核心 Pagination：它只注册进 admin bundle，forum 侧 import 会
   *    抛「No module found」并中断其后所有扩展注册（见文件头禁区①）。
   *
   * 链接口径：首页用干净路径 /downloads，分类页用 /downloads/c/{slug}；
   * 其后按需拼 ?page=&sort=&dir=&q=&types=，由 app.route 生成（与后端 SPA 路由口径一致）。
   * 分页改路由 → 组件重建 → oninit 从路由参数恢复 state；故链接必须带上当前排序 / 搜索 /
   * 类型过滤，翻页后筛选状态不丢。
   */
  pager(pagination) {
    if (!pagination || pagination.totalPages <= 1) return null;
    const link = page => {
      // app.route 会 mutate 传入对象（path 参数被 extract 删除），故每次新建对象。
      // 路由按 kind 选择：分类页 = 'downloads.category'（带 slug），首页 = 'downloads'（无 slug）。
      const params = {};
      if (this.kind === 'category') params.slug = this.slug;
      if (page > 1) params.page = page;
      if (this.state.sort) params.sort = this.state.sort;
      if (this.state.dir) params.dir = this.state.dir;
      if (this.state.q) params.q = this.state.q;
      if (this.state.types.length) params.types = this.state.types.join(',');
      return this.route(this.kind === 'category' ? 'downloads.category' : 'downloads', params);
    };
    return m("nav", {
      className: "jd-pagination"
    }, pagination.prevPage ? m(LinkButton, {
      className: "jd-page-link",
      href: link(pagination.prevPage)
    }, m(Icon, {
      name: "fas fa-chevron-left"
    })) : m("span", {
      className: "jd-page-link is-disabled"
    }, m(Icon, {
      name: "fas fa-chevron-left"
    })), pagination.pages.map(p => p.current ? m("span", {
      className: "jd-page-link is-current",
      key: p.page
    }, p.page) : m(LinkButton, {
      className: "jd-page-link",
      href: link(p.page),
      key: p.page
    }, p.page)), pagination.nextPage ? m(LinkButton, {
      className: "jd-page-link",
      href: link(pagination.nextPage)
    }, m(Icon, {
      name: "fas fa-chevron-right"
    })) : m("span", {
      className: "jd-page-link is-disabled"
    }, m(Icon, {
      name: "fas fa-chevron-right"
    })));
  }

  /**
   * 统一风格的提示页（错误 / 无权限 / 不存在 / 需登录）。
   *
   * 文案优先用后端返回的 error.message（已按语言包本地化），
   * 后端未给时回落到前端按错误码映射的兜底文案。
   */
  noticeView() {
    const code = this.errorCode ?? 'ERROR';

    // 错误码 → [标题键, 正文键]（与后端 DownloadDataController::error 的 code 对齐）。
    const map = {
      DISABLED: ['notice_disabled_title', 'notice_disabled'],
      FORBIDDEN: ['notice_forbidden_title', 'notice_forbidden'],
      NOT_FOUND: ['notice_not_found_title', 'notice_not_found']
    };
    const [titleKey, messageKey] = map[code] ?? ['notice_error_title', 'notice_error'];
    const isGuest = !app.session.user;
    return m("div", {
      className: "jd-notice"
    }, m("span", {
      className: "jd-notice-icon"
    }, m(Icon, {
      name: "fas fa-exclamation-triangle"
    })), m("h1", {
      className: "jd-notice-title"
    }, this.t(titleKey)), m("p", {
      className: "jd-notice-message"
    }, this.errorMessage || this.t(messageKey)), m("div", {
      className: "jd-notice-actions"
    }, m(LinkButton, {
      className: "jd-btn jd-btn-ghost",
      href: this.route('downloads')
    }, m(Icon, {
      name: "fas fa-chevron-left"
    }), this.t('action_back_downloads')), isGuest ? m(Button, {
      className: "jd-btn jd-btn-primary",
      onclick: () => this.login()
    }, this.t('action_login')) : null));
  }
}
flarum.reg.add('jiedh-downloads', 'forum/components/DownloadPage', DownloadPage);flarum.reg.addChunkModule('89', '217', 'jiedh-downloads', 'forum/components/ReportResourceModal');
flarum.reg.addChunkModule('350', '446', 'jiedh-downloads', 'forum/components/UploadResourceModal');